import { useEffect } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import LoginLayout from './layout/login-layout';
import * as S from './styles';
import { StepLoginForm } from '../../../components/forms/login/step-login-form';
import { useGenericMutation } from '../../../services/react-query/generic.mutation';
import { AuthTypes } from '../../../types/auth.types';
import Loading from '../../../components/loading';
import { handleApiError } from '../../../services/react-query/react-query.client';
import { toast } from 'react-toastify';
import { store } from '../../../store';
import { isRequestCanceled } from '../../../http';
import { startAuthenticatedReplay } from '../../../services/sentry-replay.service';
import { safeRedirectPath } from '../../../helpers/safeRedirectPath';


const Login: React.FC = () => {
    const navigate = useNavigate();
    const currentLocation = useLocation();

    const { mutateAsync: validateSsoLogin, isLoading: validateSsoLoading } = useGenericMutation<{ accessToken: string }, AuthTypes.LoginResult>('authValidateSsoLogin', 'auth/sso', 'post');

    const getAccessTokenFromUrl = () => {
        const params = new URLSearchParams((currentLocation)?.hash?.substring(1));
        const accessToken = params.get('access_token');
  
        return accessToken;
    };

    useEffect(() => {
        const accessToken = getAccessTokenFromUrl();
        let ignore = false;

        if (accessToken) {
            (async () => {
                await validateSsoLogin({ accessToken }, {
                    onError: (error) => {
                        if (ignore || isRequestCanceled(error)) {
                            return;
                        }

                        const msg = handleApiError(error);

                        toast(msg, {
                            autoClose: 1500,
                            type: 'error',
                        });
                    },
                    onSuccess: (response) => {
                        if (ignore) {
                            return;
                        }

                        if (!response.isValid) {
                            const msg = handleApiError(response.message);
    
                            toast(msg, {
                                autoClose: 1500,
                                type: 'error',
                            });
                            return;
                        }
    
                        if (response.result.subscription) {
                            store.dispatch.customer.setSelectedSubscription(
                                response.result.subscription
                            );
                        }
                        
                        store.dispatch.auth.auth(response?.result?.user, response?.result?.token);
                        Sentry.setUser({ id: response?.result?.user?.id, email: response?.result?.user?.email, username: response?.result?.user?.fullname });
                        startAuthenticatedReplay();

                        toast(
                            'Login successfully!',
                            {
                                autoClose: 1500,
                                type: 'success',
                            },
                        );

                        // Same `state.from` passthrough as step-login-form.tsx
                        // (Pollen review, round 4, Low item) — an SSO login
                        // triggered from a deep link (a stale-token redirect
                        // to /login) should also land back where it came from.
                        const from = safeRedirectPath((currentLocation.state as { from?: string } | null)?.from);
                        navigate(from || '/', { replace: true });
                    }
                });
            })();
        }

        return () => {
            ignore = true;
        };
    }, []);

    return (
        <LoginLayout>
            <S.LoginContainer>
                {validateSsoLoading && 
                    <>
                        <Stack direction={'row'} alignItems="center">
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h5">
                                    Validating SSO access...
                                </Typography>
                                <Typography color="grayMedium.main" mt="10px" variant="body1">
                                    Wait for a moment, we're validating your SSO session.
                                </Typography>
                            </Box>
                        </Stack>
                        <S.FormContainer>
                            <Loading />
                        </S.FormContainer>
                    </>
                }
                {!validateSsoLoading && 
                    <>
                        <Stack direction="row" alignItems="center">
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h5">Our welcome!</Typography>
                                <Typography color="grayMedium.main" mt="10px" variant="body1">
                                    To access the dashboard, just fill the input below
                                </Typography>
                            </Box>
                        </Stack>
                        <S.FormContainer>
                            <StepLoginForm />
                        </S.FormContainer>
                    </>
                }
               
            </S.LoginContainer>
        </LoginLayout>
    )
}

export default Login;
